/**
 * Media sharing — turn a generated media message into a public link (wisetree.ai/m/{mediaId}).
 * `view` = content-only viewer; `invite` = viewer + a join-the-chat CTA. getMediaShare is public
 * (the viewer page calls it for anonymous visitors).
 */
import { callFunction } from '../utils/apiClient';

export type ShareMode = 'view' | 'invite';

export interface MediaShareView {
  mediaId: string;
  kind: 'image' | 'video' | 'audio';
  mime: string;
  title: string;
  mode: ShareMode;
  conversationId: string;
  mediaUrl: string;
}

/** Owner/participant creates (or re-modes) the share; returns the slug to build the link from. */
export const createMediaShare = (conversationId: string, messageId: string, mode: ShareMode) =>
  callFunction<{ mediaId: string; mode: ShareMode }>('createMediaShare', { conversationId, messageId, mode });

/** Public viewer payload for /m/{mediaId}. */
export const getMediaShare = (mediaId: string) =>
  callFunction<MediaShareView>('getMediaShare', { mediaId }, { requireAuth: false });

/** Canonical public link for a media id (uses the current origin — prod = wisetree.ai). */
export const mediaShareUrl = (mediaId: string) => `${window.location.origin}/m/${mediaId}`;

/** Signed-in viewer joins the chat behind an 'invite' share. Returns the slug to navigate into. */
export const acceptMediaShareInvite = (mediaId: string) =>
  callFunction<{ conversationId: string; slug: string }>('acceptMediaShareInvite', { mediaId });

// ── Analytics + assets (Step 4/5) ──────────────────────────────────────────────

export interface MediaAssetSummary {
  mediaId: string; kind: 'image' | 'video' | 'audio';
  /** Owner-editable creation title (null → show the short media id). */
  name?: string | null;
  /** The group it was made in — caption + link. */
  group_title?: string | null; group_slug?: string | null; conversation_id?: string;
  mode: ShareMode; storage_path?: string | null; views_total: number; unique_viewers: number; created_at: string | null;
}
export interface MediaAssetStats {
  mediaId: string; kind: string; title: string; views_total: number; unique_viewers: number; likes_total: number;
  windowDays: number; windowViews: number; series: { day: string; views: number }[]; geo: Record<string, number>;
}
export interface TrendingItem {
  media_id: string; title: string; kind: string; owner_uid: string; views_total: number; recent_views: number; score: number;
}

/** Record a view (public) — fire-and-forget after the viewer has dwelled past the threshold. */
export const recordMediaView = (mediaId: string) =>
  callFunction<{ counted: boolean }>('recordMediaView', { mediaId }, { optionalAuth: true }).catch(() => undefined);

export const likeMediaShare = (mediaId: string, liked: boolean) =>
  callFunction<{ liked: boolean }>('likeMediaShare', { mediaId, liked });

/** Owner renames a creation (the tile title). Empty string clears it back to the id. */
export const renameMediaAsset = (mediaId: string, name: string) =>
  callFunction<{ mediaId: string; name: string | null }>('renameMediaAsset', { mediaId, name });

/** Owner deletes a creation — removes it from Saved Creations + disables its share link. */
export const deleteMediaAsset = (mediaId: string) =>
  callFunction<{ mediaId: string; deleted: boolean }>('deleteMediaAsset', { mediaId });

export const listMyMediaAssets = (kind?: 'image' | 'video' | 'audio', before?: string | null) =>
  callFunction<{ assets: MediaAssetSummary[]; nextCursor: string | null }>(
    'listMyMediaAssets', { ...(kind ? { kind } : {}), ...(before ? { before } : {}) },
  );

export const getMediaAssetStats = (mediaId: string, days: 7 | 30 | 90) =>
  callFunction<MediaAssetStats>('getMediaAssetStats', { mediaId, days });

export const getTrendingMedia = () =>
  callFunction<{ items: TrendingItem[] }>('getTrendingMedia', {}).then((r) => r.items || []);
